import { toast } from '@/components/toast';
import { sortSentiments } from '@/pages/AdvancedEditor/utils/expressive-utils';
import { useAnalytics } from '@/services/analytics';
import { getPresenters, GetPresentersByStatus } from '@/services/api';
import { useAuth } from '@/services/auth';
import { useFlag } from '@/services/featureFlags';
import { logger } from '@/services/logger';
import { GET_PRESENTERS_KEY, GET_PRESENTERS_KEY_TYPE } from '@/state/presenters';
import {
    AvatarStatus,
    checkProductMatch,
    ImageType,
    IPresenter,
    isDvpTrain,
    isPremiumPresenter,
    isStreamablePresenter,
    Presenter,
    PresenterUsecase,
    VideoType,
} from '@d-id/types';
import { useQuery } from 'react-query';

const INITIAL_PRESENTERS = 14;

// Map of presenters that have been renamed - should be the opposite of https://github.com/de-id/serverless/blob/b76c9c12d988a1326b7abd6f1e6a7e802d4db207/packages/common/utils/src/constants.ts#L71
export const MAP_PRESENTERS: Record<string, string> = {
    'v2_public_Lily@hL3ExHrIFu': 'v2_public_lily@addf3c9auh',
};

export interface GetPresentersProps {
    usecase?: PresenterUsecase;
    presenterType?: ImageType;
    status?: GetPresentersByStatus;
    includeNotReady?: boolean;
}

export function useGetPresentersList(
    { status, usecase, presenterType, includeNotReady }: GetPresentersProps = { usecase: PresenterUsecase.Video, includeNotReady: false }
) {
    const { isAnonymous, user } = useAuth();
    const modelsBlacklist = useFlag<string[]>('presenters_models_exclude', []);
    const includeExpressive = useFlag('expressive_avatars', false);
    const mixpanel = useAnalytics();

    const type = usecase === PresenterUsecase.Agent ? ImageType.WithAgents : presenterType || ImageType.Upload;

    const isModelAllowed = (presenter: IPresenter) => {
        return !modelsBlacklist.includes(presenter.id) && (presenter.id.startsWith('v2_') || !presenter.id.startsWith('v2_'));
    };

    const queryKey = [GET_PRESENTERS_KEY, GET_PRESENTERS_KEY_TYPE(type), status];

    return useQuery(queryKey, getPresenters({ type, isAnonymous, status, mixpanel, includeExpressive }), {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        placeholderData: Array.from({ length: INITIAL_PRESENTERS }, (_, i) => ({
            created_at: '',
            created_by: '',
            id: `${i}`,
            name: 'pixel',
            order: i,
            poster: '',
            thumbnail: '',
            url: '',
            type: VideoType.TALK,
            isDefault: true,
            presenterType: ImageType.Skeleton,
            prompt: '',
            seed: '',
            source_duration: -1,
            source_url: '',
            status: '' as AvatarStatus,
            consent_id: '',
            previewUrl: '',
            is_green_screen: false,
        })),
        select(presenters) {
            const orderedPresenters: Presenter[] = presenters
                .filter(presenter => !isDvpTrain(presenter))
                .filter(presenter => !isPremiumPresenter(presenter) || isModelAllowed(presenter))
                .filter(presenter => usecase !== PresenterUsecase.Agent || isStreamablePresenter(presenter))
                .filter(presenter => usecase === PresenterUsecase.Agent || !presenter?.labels?.includes('agents-only'))
                .sort((a, b) => {
                    if (a.type === VideoType.CLIP && b.type === VideoType.CLIP) {
                        return a.order - b.order;
                    }
                    return b.order - a.order;
                })
                .map(presenter => {
                    const isAiGenerated = presenter?.labels?.includes('ai-generated');

                    if (presenter.sentiments) {
                        presenter.sentiments = sortSentiments(presenter.sentiments);
                    }

                    return {
                        ...presenter,
                        is_aiGenerated: isAiGenerated,
                        id: MAP_PRESENTERS[presenter.id] ?? presenter.id,
                        presenterType: presenter.presenterType ?? ImageType.Upload,
                    };
                });

            const notReady = presenters.filter(presenter => isDvpTrain(presenter) && presenter.status !== 'done');
            if (includeNotReady && notReady.length > 0) {
                orderedPresenters.splice(0, 0, ...notReady);
            }

            if (checkProductMatch.LITE(user?.planGroup ?? user?.productName)) {
                // moving 2 sp presenters before the sp+ presenters
                const firstSPPlusPresenterIndex = orderedPresenters.findIndex(presenter => presenter.type === VideoType.CLIP);
                const firstSPPresenterIndex = orderedPresenters.findIndex(
                    presenter => presenter.type === VideoType.TALK && presenter.isDefault
                );
                const firstTwoSPPresenters = orderedPresenters.splice(firstSPPresenterIndex, 2);
                orderedPresenters.splice(firstSPPlusPresenterIndex, 0, ...firstTwoSPPresenters);
                orderedPresenters.forEach((presenter, index) => (presenter.order = orderedPresenters.length - index));
            }
            return orderedPresenters as Presenter[];
        },
        onError(err) {
            logger.error('Could not fetch presenters', { err });
            toast.error({ title: 'Could not fetch presenters', toastId: `${GET_PRESENTERS_KEY_TYPE(type)}-error` });
        },
    });
}
